<script lang="ts" setup>
import type { Props } from '@/widgets/codeInput/model/types/codeInput';

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', val: string): void;
  (e: 'update:error'): void;
}>();

const digits = ref<string[]>(Array(props.length).fill(''));
const inputs: HTMLInputElement[] = [];

const setInput = (el: HTMLInputElement | null, idx: number) => {
  if (el) inputs[idx] = el;
};

const onInput = (idx: number, e: Event) => {
  const input = inputs[idx];
  if (!input) return;
  const v = input.value.replace(/\D/g, '');
  input.value = v;
  digits.value[idx] = v;
  if (v && idx < props.length - 1) {
    inputs[idx + 1].focus();
  }
};

const onBackspace = (idx: number) => {
  if (digits.value[idx] === '' && idx > 0) {
    digits.value[idx - 1] = '';
    inputs[idx - 1]?.focus();
  } else {
    digits.value[idx] = '';
  }
};

const onPaste = (idx: number, e: ClipboardEvent) => {
  const text = e.clipboardData?.getData('text') || '';
  const onlyDigits = text.replace(/\D/g, '');
  if (!onlyDigits) return;
  for (let i = 0; i < onlyDigits.length && idx + i < props.length; i++) {
    digits.value[idx + i] = onlyDigits[i];
    if (inputs[idx + i]) inputs[idx + i].value = onlyDigits[i];
  }
  const next = Math.min(props.length - 1, idx + onlyDigits.length);
  inputs[next]?.focus();
};

watch(
  () => props.modelValue,
  val => {
    const chars = val?.split('').filter(ch => /\d/.test(ch)) || [];
    for (let i = 0; i < props.length; i++) {
      digits.value[i] = chars[i] || '';
      if (inputs[i]) inputs[i].value = digits.value[i];
    }
  },
  { immediate: true },
);

watch(
  digits,
  newArr => {
    emit('update:modelValue', newArr.join(''));
  },
  { deep: true },
);
</script>

<template>
  <div class="code-input">
    <div class="code-input__input-wrapper">
      <input
        v-for="(_, idx) in length"
        :key="idx"
        :class="['code-input__input', { _error: error }]"
        type="text"
        inputmode="numeric"
        pattern="[0-9]*"
        maxlength="1"
        :value="digits[idx]"
        :ref="
          (el: any) => {
            if (el) {
              setInput(el as HTMLInputElement, idx);
            }
          }
        "
        @input="onInput(idx, $event)"
        @keydown.backspace.prevent="onBackspace(idx)"
        @paste.prevent="onPaste(idx, $event)"
      />
    </div>
    <span v-if="error" class="code-input__error">{{ error }}</span>
  </div>
</template>

<style lang="less">
.code-input {
  &__input-wrapper {
    display: grid;
    grid-template-columns: repeat(v-bind('props.length'), 1fr);
    gap: @size-x3;
    justify-content: center;
    align-items: center;
    width: 100%;
  }

  &__input {
    .input-reset();

    font-size: @size-x6;
    font-weight: 500;
    line-height: 100%;
    text-align: center;
    border-radius: @size-x3;
    background: @color-athens-gray;
    border: none;
    outline: none;
    height: 56px;
    width: 100%;

    &._error {
      border: 1px solid @color-carnation;
    }
  }

  &__error {
    display: block;
    font-size: 14px;
    line-height: 130%;
    font-weight: 400;
    color: @color-carnation;
    text-align: center;
    margin-top: @size-x4;
  }

  @media @1024 {
    &__input-wrapper {
      gap: @size-x2;
    }

    &__input {
      height: 45px;
    }
  }
}
</style>
